import type { ApiClient } from '../../../shared/api/api-client';
import type { OrgBranchTenantRow } from './organization-branches.types';

function asStr(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v).trim();
  return s === '' ? null : s;
}

function asPositiveInt(v: unknown): number | null {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : Number(String(v).trim());
  if (!Number.isFinite(n)) return null;
  const t = Math.trunc(n);
  return t >= 1 && t <= 24 * 60 ? t : null;
}

export function parseTenantBranchRow(raw: unknown): OrgBranchTenantRow | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = typeof r.id === 'number' ? r.id : Number(r.id);
  const name = typeof r.name === 'string' ? r.name.trim() : '';
  if (!Number.isFinite(id) || id < 1 || !name) return null;
  return {
    id,
    name,
    address: asStr(r.address),
    phone: asStr(r.phone),
    timezone: asStr(r.timezone),
    schedule_slot_step_minutes: asPositiveInt(r.schedule_slot_step_minutes),
    is_active: Boolean(r.is_active),
    working_hours: asStr(r.working_hours),
    working_weekdays: Array.isArray(r.working_weekdays)
      ? r.working_weekdays.map((x) => Number(x)).filter((n) => Number.isInteger(n) && n >= 0 && n <= 6)
      : null,
    active_payment_gateway: asStr(r.active_payment_gateway),
    robokassa_enabled: r.robokassa_enabled !== undefined ? Boolean(r.robokassa_enabled) : undefined,
    yookassa_enabled: r.yookassa_enabled !== undefined ? Boolean(r.yookassa_enabled) : undefined,
    tochka_enabled: r.tochka_enabled !== undefined ? Boolean(r.tochka_enabled) : undefined
  };
}

/** GET /organizations/me/branches — канонический список филиалов текущей организации. */
export async function fetchTenantBranches(client: ApiClient): Promise<OrgBranchTenantRow[]> {
  const raw = await client.request<unknown>('/organizations/me/branches', { method: 'GET' });
  if (!Array.isArray(raw)) return [];
  const out: OrgBranchTenantRow[] = [];
  for (const row of raw) {
    const p = parseTenantBranchRow(row);
    if (p) out.push(p);
  }
  return out;
}

export async function postTenantBranch(
  client: ApiClient,
  body: Record<string, unknown>
): Promise<OrgBranchTenantRow | null> {
  const raw = await client.request<unknown>('/organizations/me/branches', { method: 'POST', body });
  return parseTenantBranchRow(raw);
}

export async function patchTenantBranch(
  client: ApiClient,
  branchId: number,
  body: Record<string, unknown>
): Promise<OrgBranchTenantRow | null> {
  const raw = await client.request<unknown>(`/organizations/me/branches/${branchId}`, { method: 'PATCH', body });
  return parseTenantBranchRow(raw);
}

/** GET /organizations/me/branches/{id} — полная карточка (платёжные поля и флаги секретов). */
export async function fetchTenantBranchById(client: ApiClient, branchId: number): Promise<unknown> {
  return client.request<unknown>(`/organizations/me/branches/${branchId}`, { method: 'GET' });
}
